import { useCallback, useEffect } from "react"
import { useForm, type Path, type PathValue } from "react-hook-form"
import { useDrawer } from "@contexts/DrawerContext"
import { useGetRevenueClassesQuery } from "@apis/shared/useShared"
import { useGetMarketsByZipCodeQuery } from "@apis/offers/useOffers"
import { useGetCampaignsQuery } from "@apis/campaigns/useCampaigns"
import { Button } from "@components/ui/button"
import { toast } from "sonner"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import { AddressInfoForm } from "@components/Address/AddressInfoForm"
import { useNewOrderFunnel } from "../utils/useNewOrderFunnel"
import type { IAddress } from "@commonTypes/address"
import type { IOffersRequest } from "@commonTypes/offer"
import { useCreateShopperMutation } from "@apis/shoppers/useShoppers"
import { useUtils } from "../utils/utils"
import { Loader2 } from "lucide-react"

type TFormValues = {
  address: IAddress | string
  service_class: string
  utility: string
  campaign: string
}

export const NewOrderForm = () => {
  const {
    prospect,
    setOffersParams,
    setStep,
    setSelectedMarket,
    setSelectedRevenueClassCode,
    selectedAddress,
    setSelectedAddress,
    shopper,
    setShopper,
  } = useNewOrderFunnel()
  const { openDrawer, closeDrawer } = useDrawer()
  const { handleSaveAddress } = useUtils()
  const getRevenueClassQuery = useGetRevenueClassesQuery()

  const form = useForm<TFormValues>({
    mode: "onTouched",
    defaultValues: {
      address: "",
      service_class: "",
      utility: "",
      campaign: "",
    },
  })

  // Helper to avoid duplication setting form values with flags
  const setFormValue = useCallback(
    <Field extends Path<TFormValues>>(
      name: Field,
      value: PathValue<TFormValues, Field>,
      shouldDirty = false,
      shouldTouch = false,
      shouldValidate?: boolean,
    ) => {
      form.setValue(name, value, {
        shouldDirty,
        shouldTouch,
        shouldValidate,
      })
    },
    [form],
  )

  useEffect(() => {
    if (!selectedAddress && prospect?.addresses?.length) {
      const first = prospect?.addresses?.[0]

      if (first) {
        setSelectedAddress(first)
        setFormValue("address", first.address_id.toString(), false, false)
        setFormValue("utility", "", false, false)
        setSelectedMarket(undefined)
      }
    }
  }, [
    form,
    prospect?.addresses,
    selectedAddress,
    setSelectedAddress,
    setSelectedMarket,
    setFormValue,
  ])

  // If shopper.service_address appears or changes, replace the selection even if selectedAddress already exists. This can happen when clicking from Partials in Prospect Page (we have the shopper already created)
  useEffect(() => {
    const shopperAddr = shopper?.service_address
    if (!shopperAddr?.address_id) return

    if (
      !selectedAddress ||
      selectedAddress.address_id !== shopperAddr.address_id
    ) {
      setSelectedAddress(shopperAddr)
      setFormValue("address", shopperAddr.address_id.toString(), false, false)
      setFormValue("utility", "", false, false)
      setSelectedMarket(undefined)
    }
  }, [
    form,
    selectedAddress,
    setSelectedAddress,
    setSelectedMarket,
    shopper?.service_address,
    setFormValue,
  ])

  const campaignsParams = {
    active: true,
    show_to_agents_flag: true,
    source_code: "INSPIRE",
  }

  const getMarketsByZipCodeQuery = useGetMarketsByZipCodeQuery(
    selectedAddress?.zip,
  )
  const getCampaignsQuery = useGetCampaignsQuery(campaignsParams)
  const createShopperMutation = useCreateShopperMutation()

  useEffect(() => {
    if (!shopper) return

    if (shopper.revenue_class_code) {
      setFormValue(
        "service_class",
        shopper.revenue_class_code,
        false,
        false,
        true,
      )
      setSelectedRevenueClassCode(shopper.revenue_class_code)
    }

    if (shopper.market_code) {
      setFormValue("utility", shopper.market_code, false, false, true)
      const market = getMarketsByZipCodeQuery?.data?.find(
        (mkt) => mkt.market_code === shopper.market_code,
      )
      if (market) setSelectedMarket(market)
    }

    if (shopper.campaign_id) {
      setFormValue("campaign", shopper.campaign_id, false, false, true)
    }
  }, [
    shopper,
    getMarketsByZipCodeQuery?.data,
    form,
    setSelectedRevenueClassCode,
    setSelectedMarket,
    setFormValue,
  ])

  const handleSubmit = (data: TFormValues) => {
    if (!prospect || !selectedAddress) return
    const params: IOffersRequest = {
      account_number: prospect.account_number,
      campaign_id: data.campaign,
      market_code: data.utility,
      prospect_guid: prospect.prospect_guid,
      return_offer_details: true,
      revenue_class_code: data.service_class,
      service_address_line_1: selectedAddress.street,
      service_address_line_2: selectedAddress.unit,
      service_city: selectedAddress.city,
      service_state: selectedAddress.state_code,
      service_zip_code: selectedAddress.zip,
      source_code: "INSPIRE",
    }
    const shopper_request = {
      ...prospect,
      ...shopper, // If shopper is defined/exists, we’ll prefill/update; otherwise, we’ll create one from the prospect.
      ...params,
    }

    createShopperMutation.mutate(
      { ...shopper_request },
      {
        onSuccess: (shopper) => {
          setOffersParams(params)
          setShopper(shopper)
          setStep("chooseOffer")
        },
        onError: (error) =>
          toast.error(`Error creating shopper! Error: ${error}`),
      },
    )
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="tw:flex tw:flex-col tw:gap-4"
      >
        <FormField
          control={form.control}
          name="address"
          rules={{ required: "Address is required" }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address</FormLabel>
              <Select
                value={typeof field.value === "string" ? field.value : ""}
                onValueChange={(value) => {
                  field.onChange(value)
                  const fullAddress = prospect?.addresses?.find(
                    (addr) => addr.address_id.toString() === value,
                  )
                  setSelectedAddress(fullAddress || undefined)
                }}
              >
                <FormControl className="tw:w-full">
                  <SelectTrigger>
                    <SelectValue placeholder="Select address" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {prospect?.addresses?.map((address) => (
                    <SelectItem
                      key={address.address_id}
                      value={address.address_id.toString()}
                    >
                      {address.street}, {address.city}, {address.state_code}{" "}
                      {address.zip}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
              <Button
                type="button"
                size="sm"
                variant="default"
                onClick={() =>
                  openDrawer(
                    <AddressInfoForm
                      identifier={prospect?.prospect_guid as string}
                      address_type_code="prospect"
                      onSave={() =>
                        handleSaveAddress(
                          prospect?.prospect_guid as string,
                          closeDrawer,
                        )
                      }
                      onCancel={() => closeDrawer()}
                    />,
                    "Address Information",
                  )
                }
              >
                Add Address
              </Button>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="service_class"
          rules={{ required: "Class is required" }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Class</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value)
                  setSelectedRevenueClassCode(value)
                }}
                value={field.value}
              >
                <FormControl className="tw:w-full">
                  <SelectTrigger>
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {getRevenueClassQuery?.data?.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="utility"
          rules={{ required: "Utility is required" }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Utilities servicing zip code</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value)
                  const market = getMarketsByZipCodeQuery?.data?.find(
                    (market) => market.market_code.toString() === value,
                  )
                  setSelectedMarket(market || undefined)
                }}
                value={field.value}
              >
                <FormControl className="tw:w-full">
                  <SelectTrigger>
                    <SelectValue placeholder="Select utility" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {getMarketsByZipCodeQuery?.data?.map((item) => (
                    <SelectItem key={item.market_code} value={item.market_code}>
                      {item.market_code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="campaign"
          rules={{ required: "Campaign is required" }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Campaign</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl className="tw:w-full">
                  <SelectTrigger>
                    <SelectValue placeholder="Select campaign" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {getCampaignsQuery.data?.map((item) => (
                    <SelectItem key={item.campaign_id} value={item.campaign_id}>
                      {item.campaign_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          className="tw:w-1/2 tw:self-end tw:bg-green-600 tw:hover:bg-green-600/80"
          disabled={createShopperMutation.isPending || !form.formState.isValid}
        >
          {createShopperMutation.isPending ? (
            <Loader2 className="tw:animate-spin" />
          ) : (
            <span>Show Offers</span>
          )}
        </Button>
      </form>
    </Form>
  )
}
